export const profile = {
  name: "Alexander Risio",
  email: "ajrisio@college.harvard.edu",
  linkedin: "https://www.linkedin.com/in/alexanderrisio/",
  github: "https://github.com/AlexRisio",
  resume:
    "https://docs.google.com/document/d/1jf-9BEmPIR6p-b7Rrwrlt1-5-NppXit9GU2sdja4cdg/edit",
};

export const projects = [
  {
    id: "track-pacing-robot",
    name: "Track-pacing robot",
    field: "Running meets robotics",
    role: "Personal project",
    summary: "I built a track-pacing robot, bringing running and technology into the same project.",
    body: "I like solving difficult problems and learning throughout the process. This is one of the things I’ve made outside work, and I filed a provisional patent application for it.",
    links: [],
  },
  {
    id: "primeshio",
    name: "Primeshio",
    field: "AI-native 3D modeling",
    role: "Co-founder",
    period: "Earlier venture",
    image: "primeshio.webp",
    summary:
      "An AI-powered modeling copilot for Blender, built from a love of 3D.",
    body: "Primeshio explored how an AI modeling copilot could help people work in Blender. It grew out of my interest in 3D modeling, generative geometry, and the tools that make creative work possible.",
    detail: "The project received an in-person Y Combinator interview.",
    links: [{ name: "Visit Primeshio", url: "https://www.primeshio.com/" }],
  },
  {
    id: "arckis",
    name: "Arckis",
    field: "Public-sector data infrastructure",
    role: "Co-founder",
    period: "Earlier venture",
    image: "arckis.webp",
    summary:
      "A better data rail for communication between government agencies.",
    body: "Arckis focused on the infrastructure between government agencies: how information moves, how workflows connect, and how better software could make that exchange easier.",
    detail:
      "The project received a Y Combinator interview. The source is available on GitHub.",
    links: [
      {
        name: "Explore the source",
        url: "https://github.com/AlexRisio/Arckis",
      },
    ],
  },
];

export const experience = [
  {
    id: "risklytics", name: "Risklytics", role: "Co-founder", period: "2026", tag: "YC S26",
    image: "risklytics.webp", alt: "The Risklytics website I designed and built", contain: false,
    summary: "I love creating things, and I co-founded Risklytics with my college roommate.",
    body: "We built a commercial insurance brokerage to help hardware and physical AI companies find coverage. Risklytics was backed by Y Combinator’s S26 batch.",
    detail: "I designed and built the entire Risklytics.ai website myself.",
    links: [{ name: "Visit Risklytics", url: "https://www.risklytics.ai/" }],
  },
  {
    id: "research", name: "Harvard Medical School", role: "Research assistant · HLI Lab", period: "January–May 2026",
    image: "hli-lab.webp", alt: "HLI Lab at Harvard Medical School", contain: true,
    summary: "My interest in medical science and biology brought me to the HLI Lab.",
    body: "At the HLI Lab, I implemented and benchmarked long-read alignment and variant-calling algorithms on large-scale human sequencing datasets.",
    detail: "I worked in C/C++ and Python on genome assembly and pangenome analysis pipelines, with a focus on runtime and memory efficiency.",
    links: [{ name: "Visit the HLI Lab", url: "https://hlilab.github.io/" }],
  },
  {
    id: "hdag", name: "Harvard Undergraduate Data Analytics Group", shortName: "HDAG", role: "Director of Case Affairs", period: "September 2025–May 2026",
    image: "hdag.webp", alt: "Harvard Undergraduate Data Analytics Group", contain: true,
    summary: "I joined for the data analytics and found a great community along the way.",
    body: "I led client work across biotech, healthcare, and finance, closed over $40,000 in deals, and led machine learning and statistical analysis work.",
    detail: "The work brought together client relationships, research, analysis, and technical delivery.",
    links: [{ name: "Visit Harvard Analytics", url: "https://harvardanalytics.org/" }],
  },
  {
    id: "crimson", name: "The Harvard Crimson", role: "Technology board member", period: "January 2025–May 2026",
    image: "crimson.webp", alt: "The Harvard Crimson", contain: true,
    summary: "I wanted to work on technology with a public audience. The Crimson let me bring that interest into media.",
    body: "I worked on a next-generation Svelte website, critical fixes, and interactive article features for a publication reaching over 600,000 unique users.",
    detail: "My contributions spanned front-end and back-end development and data visualizations.",
    links: [{ name: "Read The Harvard Crimson", url: "https://www.thecrimson.com/" }],
  },
  {
    id: "cory-reading", name: "Decodables (now Cory Reading)", shortName: "Cory Reading", role: "Lead data analyst", period: "August 2024–June 2025",
    image: "cody-reading.webp", alt: "Reading platform artwork from my time at Decodables", contain: true,
    summary: "Working with Cory Reading was a way to give back to the community.",
    body: "I worked on AI models and reading-pattern analysis for personalized support for dyslexic students.",
    detail: "My role focused on data analysis and model training for reading support.",
    links: [],
  },
];

export const introduction = "I like many things: building, music, film, and figuring out where technology is going. I enjoy solving difficult problems and learning along the way.";

export const narrative = {
  welcome: "Co-founded Risklytics. I like building things and figuring out what technology can do next.",
  studies: "I’m pursuing a dual A.B./S.M. in Computer Science at Harvard.",
  curiosity: "Lately, I’ve been curious about marine biology, the future of robotics, and David Lynch’s work.",
  desktop: "Stardew Valley is my favorite game. I love nature and nostalgic technology, so this little desktop felt like a fitting place to bring my interests together.",
  day: "A good day might be a run through nature, some time on a side project or music, and baking or cooking something for dinner.",
  running: "Running keeps me physically healthy. I especially love a route through nature.",
  cooking: "Cooking clears my head. I find it meditative.",
  music: "I’m learning music as another way to be creative.",
  contact: "If something in my work, experience, or projects caught your attention, I’d love to hear from you.",
};

// Included in the initial HTML so professional content survives blocked or failed JavaScript.
export function fallbackContent() {
  return `<article id="static-profile"><header><img src="/img/alex-risio.webp" width="120" height="150" alt="Alexander Risio" fetchpriority="high"><h1>Alexander Risio</h1><p>${introduction}</p><p>${narrative.studies}</p><p>${narrative.curiosity}</p><nav><a href="#static-experience">Experience</a><a href="${profile.resume}">Résumé</a><a href="mailto:${profile.email}">Contact</a></nav></header><h2 id="static-experience">Experience</h2>${experience.map(p=>`<section id="static-${p.id}"><img src="/img/${p.image}" width="320" height="180" loading="lazy" alt="${p.alt}"><h3>${p.name}</h3><p><strong>${p.role}</strong> · ${p.period}</p><p>${p.summary}</p><p>${p.body}</p><p>${p.detail}</p>${p.links.map(l=>`<a href="${l.url}">${l.name}</a>`).join('')}</section>`).join('')}<h2>Projects</h2>${projects.map(p=>`<section><h3>${p.name}</h3><p>${p.summary}</p><p>${p.body}</p>${p.detail ? `<p>${p.detail}</p>` : ''}${p.links.map(l=>`<a href="${l.url}">${l.name}</a>`).join('')}</section>`).join('')}<footer><a href="${profile.linkedin}">LinkedIn</a> · <a href="${profile.github}">GitHub</a> · <a href="/archive/">Original portfolio</a></footer></article>`;
}
